import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Boxes, Languages, FlaskRound as Flask, Network } from 'lucide-react';
import { GlassCard } from './components/GlassCard';
import { QuestionCard } from './components/QuestionCard';
import { getRandomQuestions, getQuestionCount } from './data/questions';
import { languages } from './data/i18n';
import { ResultPoster } from './components/ResultPoster';
import { Logo } from './components/Logo';
import { Background3D } from './components/Background3D';
import { IndustrySelector } from './components/IndustrySelector';
import { processAssessmentResult } from './data/tagExtractor';
import { ProductIntro } from './components/ProductIntro';
import type { AssessmentResult, Question } from './types';

function Assessment() {
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [language, setLanguage] = useState<'zh' | 'en'>('zh');
  const [category, setCategory] = useState<'AI' | 'Web3' | 'both' | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [showIndustrySelector, setShowIndustrySelector] = useState(false);

  const t = languages[language];

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'zh' ? 'en' : 'zh');
  };

  const handleCategorySelect = (selectedCategory: 'AI' | 'Web3' | 'both') => {
    setCategory(selectedCategory);
    setShowIndustrySelector(true);
  };

  const handleStart = () => {
    if (!selectedIndustry || !category) {
      return;
    }
    const randomQuestions = getRandomQuestions(category, getQuestionCount(category));
    setQuestions(randomQuestions);
    setStarted(true);
  };

  const handleAnswer = (answerIndex: number) => {
    const newAnswers = [...answers, answerIndex];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      const correct = newAnswers.filter(
        (answer, index) => answer === questions[index].correctAnswer
      ).length;
      
      const aiQuestions = questions.filter(q => q.category === 'AI');
      const web3Questions = questions.filter(q => q.category === 'Web3');
      
      const aiCorrect = newAnswers.filter(
        (answer, index) => questions[index].category === 'AI' && 
        answer === questions[index].correctAnswer
      ).length;
      
      const web3Correct = newAnswers.filter(
        (answer, index) => questions[index].category === 'Web3' && 
        answer === questions[index].correctAnswer
      ).length;

      const score = Math.round((correct / questions.length) * 100);

      const userProfile = {
        industry: {
          primary: selectedIndustry || 'tech',
        },
        useCases: [],
        experience: {
          years: Math.floor(score / 20),
          roles: ['Learner']
        },
        interests: [category || 'both'],
        learningStyle: 'practical',
        goals: ['Improve knowledge']
      };

      const { technicalScore, applicationScore, innovationScore, technicalTags, applicationTags, innovationTags, yearsOfExperience } = processAssessmentResult(questions, newAnswers, category || 'both');
      
      setResult({
        score,
        aiScore: aiQuestions.length ? Math.round((aiCorrect / aiQuestions.length) * 100) : 0,
        web3Score: web3Questions.length ? Math.round((web3Correct / web3Questions.length) * 100) : 0,
        timestamp: Date.now(),
        answers: newAnswers,
        category: category || 'both',
        level: score >= 80 ? 'expert' : score >= 60 ? 'intermediate' : 'beginner',
        suggestions: [],
        technicalScore,
        applicationScore,
        innovationScore,
        technicalTags,
        applicationTags,
        innovationTags,
        yearsOfExperience,
        userProfile
      });
    }
  };

  const handleRetake = () => {
    setStarted(false);
    setCurrentQuestion(0);
    setAnswers([]);
    setResult(null);
    setSelectedIndustry(null);
    setCategory(null);
    setShowIndustrySelector(false);
  };

  const handleEcosystemClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Background3D />
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        {/* Header with Logo and Navigation */}
        <div className="absolute top-4 left-0 right-0 px-4 flex justify-between items-center">
          <Logo />
          
          {/* Ecosystem Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleEcosystemClick('https://labbb.aigc-dao.com')}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all"
            >
              <Flask className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-200">LabbbFunAI</span>
            </button>
            
            <button
              onClick={() => handleEcosystemClick('https://aigc-dao.com')}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all"
            >
              <Network className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-gray-200">官网</span>
            </button>

            <button
              onClick={toggleLanguage}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors ml-2"
              title={language === 'zh' ? 'Switch to English' : '切换到中文'}
            >
              <Languages className="w-5 h-5 text-gray-200" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {!started ? (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-2xl mx-auto text-center"
            >
              <GlassCard className="p-8">
                <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-600 text-transparent bg-clip-text">
                  {t.welcome.title}
                </h1>
                <div className="flex justify-center gap-6 mb-8">
                  <Brain className="w-12 h-12 text-blue-400" />
                  <Boxes className="w-12 h-12 text-purple-400" />
                </div>
                <p className="text-lg mb-8 text-gray-200">
                  {t.welcome.subtitle}
                </p>

                {/* Category Selection */}
                <div className="mb-12 p-6 bg-white/5 rounded-xl border border-white/10">
                  <h2 className="text-xl mb-6 text-gray-200">{t.welcome.categorySelection}</h2>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <button
                      onClick={() => handleCategorySelect('AI')}
                      className={`px-6 py-2 rounded-full ${
                        category === 'AI' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-white/10 text-gray-200 hover:bg-white/20'
                      } transition-colors`}
                    >
                      {t.welcome.categoryAI}
                    </button>
                    <button
                      onClick={() => handleCategorySelect('Web3')}
                      className={`px-6 py-2 rounded-full ${
                        category === 'Web3'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white/10 text-gray-200 hover:bg-white/20'
                      } transition-colors`}
                    >
                      {t.welcome.categoryWeb3}
                    </button>
                    <button
                      onClick={() => handleCategorySelect('both')}
                      className={`px-6 py-2 rounded-full ${
                        category === 'both'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                          : 'bg-white/10 text-gray-200 hover:bg-white/20'
                      } transition-colors`}
                    >
                      {t.welcome.categoryBoth}
                    </button>
                  </div>
                </div>
                
                {/* Industry Selection */}
                <AnimatePresence>
                  {showIndustrySelector && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden mb-12"
                    >
                      <div className="p-6 bg-white/5 rounded-xl border border-white/10">
                        <h2 className="text-xl mb-6 text-gray-200">选择您的行业</h2>
                        <IndustrySelector 
                          onSelect={(industry) => setSelectedIndustry(industry.primary)}
                          selectedIndustry={selectedIndustry}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Start Button */}
                <motion.button
                  onClick={handleStart}
                  disabled={!selectedIndustry || !category}
                  className={`px-12 py-4 rounded-full text-white font-semibold text-lg transition-all transform hover:scale-105 ${
                    selectedIndustry && category
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg hover:shadow-xl'
                      : 'bg-gray-500 cursor-not-allowed'
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {selectedIndustry && category ? t.welcome.startButton : '请完成选择'}
                </motion.button>
              </GlassCard>
            </motion.div>
          ) : result ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              className="w-full max-w-3xl"
            >
              <GlassCard className="p-8">
                <ResultPoster result={result} onRetake={handleRetake} />
              </GlassCard>
            </motion.div>
          ) : (
            <motion.div
              key="question"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <QuestionCard
                question={questions[currentQuestion]}
                onAnswer={handleAnswer}
                progress={(currentQuestion + 1) / questions.length * 100}
                questionNumber={currentQuestion + 1}
                totalQuestions={questions.length}
                t={t}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Assessment />} />
        <Route path="/product-intro" element={<ProductIntro />} />
      </Routes>
    </Router>
  );
}

export default App;